import React, { useEffect, useState } from "react";
import { Observer } from "mobx-react";
import DownloadModalStore from "@stores/DownloadModalStore";
import CustomTable from "@components/CustomTable";
import ButtonComponent from "@components/ButtonComponent";
import Confirmationpopup from "@components/ConfirmationPopup";
import Loader from "@components/LoaderComponent/loader";
import clsx from "clsx";
import { toast } from "react-toastify";
import { downloadFile } from "@/helpers/downloadHelper";

import { ZERO_VALUE } from "@utils/constants";
import { X } from "lucide-react";

import {
  noDataHelper,
  removeFromDownloadCenter,
  clearDownloadCenter,
  humanize,
  ExportReport,
  ExportReconciliationRecords,
  GetReconciliationFile,
  GetSFMDisplay,
  updateDownloadItem,
  addToDownloadCenter,
  extractFileName,
  extractFileSize,
} from "@helpers/helpers";
import { withTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { pollReconciliationFile } from "@helpers/apiHelper";

const DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT = 500;
const DOWNLOAD_MODAL_SET_LOADING_TIMEOUT = 1000;
const DOWNLOAD_MODAL_SET_SHOWING_TIMEOUT = 3000;
const FIRST_ELEMENT_INDEX = 0;
const ROUNDING_DECIMAL = 2;

const DownloadModal = ({ t: translate, i18n }) => {
  const location = useLocation();
  const DOWNLOAD_CENTER_KEY = "DOWNLOAD_CENTER";

  useEffect(() => {
    DownloadModalStore.setShowing(false);
  }, [location]);

  useEffect(() => {
    if (!reFetchingDownloads) {
      setReFetchingDownloads(true);
      let downloads = (
        localStorage.getItem(DOWNLOAD_CENTER_KEY)
          ? JSON.parse(localStorage.getItem(DOWNLOAD_CENTER_KEY))
          : []
      ).filter((item) => item.done === false && item.apiEndpoint);

      downloads.forEach((item) => {
        if (item.apiEndpoint === "ExportReport") {
          ExportReport(
            item.id,
            item.apiParams,
            (data) => {
              if (data.File) {
                const fileName = extractFileName(data.File);
                const fileSize = extractFileSize(data.File);
                updateDownloadItem({
                  id: item.id,
                  newProps: {
                    href: data.File,
                    title: fileName,
                    fileSize: fileSize,
                    done: true,
                  },
                });
                DownloadModalStore.open(DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT);
                DownloadModalStore.highlight(
                  DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT,
                  item.id
                );
                downloadFile(data.File, "_blank");
              }
            },
            null,
            translate("download-center.actions.download-ready"),
            item.reportType,
            item.fileRetrievalId
          );
        } else if (item.apiEndpoint === "ExportReconciliationRecords") {
          ExportReconciliationRecords(
            item.id,
            item.apiParams,
            null,
            item.activityTag,
            (data) => {
              if (data?.FileDetails?.Link) {
                const fileName = data.FileDetails.Name;
                const fileSize = data.FileDetails.Size;
                updateDownloadItem({
                  id: item.id,
                  newProps: {
                    href: data.FileDetails.Link,
                    title: fileName,
                    fileSize: fileSize,
                    done: true,
                  },
                });
                DownloadModalStore.open(DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT);
                DownloadModalStore.highlight(
                  DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT,
                  item.id
                );
                downloadFile(data.FileDetails.Link, "_blank");
              }
            },
            null,
            null,
            translate("download-center.actions.download-ready"),
            item.fileRetrievalId
          );
        } else if (item.apiEndpoint === "GetReconciliationFile") {
          const { apiOrigin, FMSTag, Key, DataAcquisitionInstanceId } =
            item?.extraProps || {};
          GetReconciliationFile(
            apiOrigin,
            FMSTag,
            Key,
            DataAcquisitionInstanceId,
            (data) => {
              const file = data.Files[FIRST_ELEMENT_INDEX];
              if (file) {
                const fileName = extractFileName(file);
                const fileSize = extractFileSize(file);
                updateDownloadItem({
                  id: item.id,
                  newProps: {
                    href: file,
                    title: fileName,
                    fileSize: fileSize,
                    done: true,
                  },
                });
                DownloadModalStore.open(DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT);
                DownloadModalStore.highlight(
                  DOWNLOAD_MODAL_STORE_OPEN_TIMEOUT,
                  item.id
                );
                downloadFile(file, "_blank");
              }
            },
            null,
            // toastId,
            null,
            translate("download-center.actions.download-ready"),
            item.id,
            item.fileRetrievalId
          );
        }
      });
    }
  }, []);

  const [reFetchingDownloads, setReFetchingDownloads] = useState(false);

  const [openConfirmationAction, setOpenConfirmationAction] = useState(false);

  const getContent = (type) => {
    let content = localStorage.getItem(DOWNLOAD_CENTER_KEY)
      ? JSON.parse(localStorage.getItem(DOWNLOAD_CENTER_KEY))
      : [];

    // sort content by creationDate then activity tag then creation time

    content.sort((a, b) => {
      const dateA = new Date(`${a.creationDate} ${a.creationTime}`);
      const dateB = new Date(`${b.creationDate} ${b.creationTime}`);
      return dateB - dateA;
    });

    const columns = [
      {
        header: translate("download-center.headers.activity"),
        accessorKey: "activityTag",
        truncate: true,
        size: "100px",
        cell: ({ row }) => {
          return (
            <span className="text-xs">
              {humanize(row.original.activityTag)}
            </span>
          );
        },
      },
      {
        header: translate("download-center.headers.requestedAt"),
        accessorKey: "creationDate",
        size: "200px",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col gap-1 items-center">
              <span className="text-gray-500 dark:text-white text-xs whitespace-nowrap">
                {row.original.creationDate}
              </span>
              <span className="text-gray-500 dark:text-white text-xs whitespace-nowrap">
                {row.original.creationTime}
              </span>
            </div>
          );
        },
      },
      {
        header: translate("download-center.headers.fileName"),
        accessorKey: "title",
        size: "130px",
        // truncate: true,
        cell: ({ row }) => {
          return (
            <span className="text-xs">
              {row.original.title.replace(/_/g, " ")}
            </span>
          );
        },
      },
      {
        header: translate("download-center.headers.fileSize"),
        accessorKey: "size",
        size: "120px",
        // truncate: true,
        cell: ({ row }) => {
          const sizeToShow = parseFloat(row.original.fileSize)
            .toFixed(ROUNDING_DECIMAL)
            .replace("0.00", " 0.01");
          return (
            <span className="text-xs whitespace-nowrap">
              {isNaN(sizeToShow) ? " " : sizeToShow + " MB"}
            </span>
          );
        },
      },
      {
        header: translate("download-center.headers.status"),
        accessorKey: "status",
        size: "50px",
        cell: ({ row }) => {
          return (
            <div className="flex gap-1 items-center flex-col">
              <span
                className={clsx(
                  "text-xs px-2 py-1 rounded-full",
                  row.original.done
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                )}
              >
                {row.original.done
                  ? translate("download-center.statuses.ready")
                  : translate("download-center.statuses.processing")}
              </span>
              {DownloadModalStore.highlighted === row.original.id && (
                <span
                  className={clsx(
                    "text-xs px-2 py-1 rounded-full bg-primary-500 text-white"
                  )}
                >
                  <span className="animate-pulse">
                    {translate("download-center.statuses.new")}
                  </span>
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: translate("download-center.headers.type"),
        accessorKey: "fileType",
        size: "50px",
      },
    ];
    const actions = {
      DOWNLOAD: {
        // actionTypeTag: "FILE_DROP",
        name: translate("download-center.actions.download"),
        hideLoading: true,
        actionFN: (row) => {
          if (row.original.href !== "#") {
            downloadFile(row.original.href, "_blank");
          }
          if (!row.original.done) {
            alert(
              "File is not ready yet, please wait for the processing to finish."
            );
          }
        },
      },
      DELETE: {
        name: translate("download-center.actions.delete"),
        hideLoading: true,
        actionFN: (row) => {
          removeFromDownloadCenter({ id: row.original.id });
          DownloadModalStore.refresh();
        },
        confirmation: true,
        confirmationMessage: translate(
          "download-center.actions.delete-confirmation"
        ),
      },
    };
    const pinningInitialState = ["pin", "status"];

    return (
      DownloadModalStore.showing && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 w-[50dvw] text-black">
            <div className="flex justify-end items-center gap-2">
              {openConfirmationAction && (
                <Confirmationpopup
                  singleAction={{
                    actionFN: () => {
                      DownloadModalStore.setLoading(true);
                      clearDownloadCenter();
                      setTimeout(() => {
                        DownloadModalStore.setLoading(false);
                      }, DOWNLOAD_MODAL_SET_LOADING_TIMEOUT);
                      setTimeout(() => {
                        DownloadModalStore.setShowing(false);
                      }, DOWNLOAD_MODAL_SET_SHOWING_TIMEOUT);
                    },
                    confirmation: true,
                    confirmationMessage: translate(
                      "download-center.actions.clear-downloads-confirmation"
                    ),
                    severity: "high",
                  }}
                  setOpenConfirmationAction={setOpenConfirmationAction}
                />
              )}
            </div>
            <CustomTable
              language={i18n.language}
              externalData={content.map((item) => {
                return {
                  ...item,
                  Actions: item.done ? ["DOWNLOAD", "DELETE"] : ["DELETE"],
                };
              })}
              columnsWithoutPin={columns}
              loading={false}
              pinCondition={(row) => {
                return false;
              }}
              pinningInitialState={pinningInitialState}
              setParentSelectedValues={() => {}}
              prefix="DownloadCenterTable"
              title={translate("download-center.title")}
              pinnedRowHeight={62}
              maxTableHeight={"calc(100dvh - 200px)"}
              showFilter={false}
              minimalTable={true}
              hideActionColumn={false}
              hidePinColumn={false}
              actions={actions}
              noData={() => noDataHelper(translate("shared.no-downloads"))}
              isExpandable={false}
              allowExpansion={false}
              additionalComponentPosition="top"
              additionalComponent={() => {
                return content.length === ZERO_VALUE ? null : (
                  <div className="flex justify-end">
                    <ButtonComponent
                      withoutborder={true}
                      id="clearDownloads"
                      color="red"
                      type="cancel"
                      onClick={() => setOpenConfirmationAction(true)}
                      ref={null}
                      icon={null}
                      width="max"
                      noTextSwap={true}
                      text={translate(
                        "download-center.actions.clear-downloads"
                      )}
                      size="sm"
                      disabled={content.length === ZERO_VALUE}
                    />
                  </div>
                );
              }}
            />
          </div>
        </div>
      )
    );
  };

  return (
    <Observer>
      {() => (
        <div
          className={clsx(
            `group`,
            DownloadModalStore.showing ? "visible" : "invisible"
          )}
        >
          <div
            className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-20 z-[50]"
            onClick={() => DownloadModalStore.close()}
          />
          <div
            className={clsx(
              `dark:bg-gray-800  fixed transition-all overflow-y-scroll h-dvh duration-500 ease-in-out z-50 bg-white text-black py-6 px-5 top-0`,
              DownloadModalStore.showing
                ? "ltr:right-0 rtl:left-0  min-w-[55%]"
                : "ltr:-right-36 rtl:-left-36  min-w-0"
            )}
          >
            <div className="flex flex-col gap-4">
              <div className="flex gap-3 justify-center items-center">
                <div className="w-full font-bold">
                  {" "}
                  {DownloadModalStore.title}
                </div>
                <button
                  onClick={() => DownloadModalStore.close()}
                  className="w-full justify-end flex"
                >
                  <X
                    size={20}
                    className="size-6 hover:text-red-800 dark:text-white dark:hover:text-red-800 transition-all duration-200"
                  />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {DownloadModalStore.loading ? (
                  <div className="w-full h-[calc(100dvh-200px)] flex items-center justify-center">
                    <Loader dots />
                  </div>
                ) : (
                  getContent()
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Observer>
  );
};

export default withTranslation()(DownloadModal);
